/**
 * MlbTeamDataContext — aggressive auto-fill cascade for the GM Dashboard.
 *
 * Nests inside <MlbTeamProvider>. When the selected team changes, this
 * context fans out (in order):
 *
 *   1. Roster fetch          → /api/data/team/{abbr}/roster                            (1 request)
 *   2. 6 paired leaderboards → /api/data/team/{abbr}/leaderboard/{metric}?paired=true  (parallel)
 *      • offense:  xwoba · xba · xslg
 *      • defense:  xwoba_against · xba_against · xslg_against
 *   3. Player profiles       → /api/data/player/{mlbam_id}/profile?season=2026         (concurrency cap = 8)
 *
 * Each phase advances `loadingPhase`. `profilesLoaded` increments per
 * profile resolution so a top-bar indicator can show "BAL · profiles 23/40".
 *
 * ── Aborts ───────────────────────────────────────────────────────────
 * A single AbortController is held in a ref. On team change OR provider
 * unmount we abort it and create a new one — every fetch in flight gets
 * cancelled cleanly with no setState-after-unmount warnings.
 *
 * ── Cache ────────────────────────────────────────────────────────────
 * Map keyed by `${abbr}:${season}`. 10-minute TTL. Hits return instantly,
 * skipping all network work — back-and-forth team flicks don't refetch.
 *
 * ── Errors ───────────────────────────────────────────────────────────
 * Per-fetch errors are non-fatal except roster — they go into
 * `partialErrors` but don't abort the cascade. The top-level `error`
 * state flips only when the roster itself fails (everything downstream
 * needs the roster).
 *
 * Re-uses `getTeamRoster`, `getTeamLeaderboard`, `getPlayerProfile`
 * from `./statcastClient` — no new helpers, no new deps.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useMlbTeam } from './MlbTeamContext';
import {
  getPlayerProfile, getTeamLeaderboard, getTeamRoster,
} from './statcastClient';

const SEASON = 2026;
const PROFILE_CONCURRENCY = 8;
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const LEADERBOARD_METRICS = [
  'xwoba',
  'xba',
  'xslg',
  'xwoba_against',
  'xba_against',
  'xslg_against',
];

const EMPTY_LEADERBOARDS = LEADERBOARD_METRICS.reduce((acc, key) => {
  acc[key] = null;
  return acc;
}, {});

const MlbTeamDataContext = createContext(null);

// Module-scoped cache (survives team-switch flicks across remounts within the
// same SPA session). Keyed by `${abbr}:${season}`.
const cache = new Map();

function getCached(abbr) {
  const entry = cache.get(`${abbr}:${SEASON}`);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
    cache.delete(`${abbr}:${SEASON}`);
    return null;
  }
  return entry;
}

function setCached(abbr, payload) {
  cache.set(`${abbr}:${SEASON}`, { ...payload, cachedAt: Date.now() });
}

function isAbortError(err) {
  return err?.name === 'AbortError';
}

/**
 * runWithConcurrency — tiny semaphore. Awaits `task(item, signal)` for each
 * item with at most `limit` in flight at once. Calls `onSettle({ item, value, error })`
 * after each task completes or fails. Returns when all have settled.
 *
 * If the signal aborts mid-flight, the remaining queue is drained without
 * dispatching new work; tasks already running have their own signal.
 */
async function runWithConcurrency(items, limit, signal, task, onSettle) {
  if (!Array.isArray(items) || items.length === 0) return;
  let cursor = 0;
  const workers = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
    while (true) {
      if (signal?.aborted) return;
      const idx = cursor;
      cursor += 1;
      if (idx >= items.length) return;
      const item = items[idx];
      try {
        const value = await task(item, signal);
        if (signal?.aborted) return;
        onSettle({ item, value, error: null });
      } catch (error) {
        if (signal?.aborted || isAbortError(error)) return;
        onSettle({ item, value: null, error });
      }
    }
  });
  await Promise.all(workers);
}

/* -------------------------------------------------------------------------- */
/* Provider                                                                   */
/* -------------------------------------------------------------------------- */

export function MlbTeamDataProvider({ children }) {
  const { team, setTeam, abbr, name } = useMlbTeam();

  // Cascade state.
  const [roster, setRoster] = useState(null);
  const [leaderboards, setLeaderboards] = useState(EMPTY_LEADERBOARDS);
  const [profiles, setProfiles] = useState(() => new Map());
  const [loadingPhase, setLoadingPhase] = useState('idle');
  const [profilesLoaded, setProfilesLoaded] = useState(0);
  const [profilesTotal, setProfilesTotal] = useState(0);
  const [error, setError] = useState(null);
  const [partialErrors, setPartialErrors] = useState([]);
  // Refetch tick — bumping this re-runs the cascade for the current team.
  const [refetchTick, setRefetchTick] = useState(0);

  // Single AbortController for the whole cascade. Reset on team change.
  const abortRef = useRef(null);
  // Track the active abbr so async callbacks can short-circuit when a newer
  // team change has overwritten them.
  const activeAbbrRef = useRef(abbr);

  const refetch = useCallback(() => {
    if (!abbr) return;
    cache.delete(`${abbr}:${SEASON}`);
    setRefetchTick((n) => n + 1);
  }, [abbr]);

  useEffect(() => {
    if (!abbr) return undefined;

    activeAbbrRef.current = abbr;

    // Abort any prior cascade — switching teams cancels every in-flight fetch.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    // Hot cache path — instant render, skip network.
    const cached = getCached(abbr);
    if (cached) {
      setRoster(cached.roster);
      setLeaderboards(cached.leaderboards);
      setProfiles(cached.profiles);
      setProfilesTotal(cached.profilesTotal);
      setProfilesLoaded(cached.profilesLoaded);
      setPartialErrors(cached.partialErrors || []);
      setError(null);
      setLoadingPhase('done');
      return () => controller.abort();
    }

    // Reset state for fresh cascade so we don't show prior team's numbers.
    setRoster(null);
    setLeaderboards(EMPTY_LEADERBOARDS);
    setProfiles(new Map());
    setProfilesLoaded(0);
    setProfilesTotal(0);
    setError(null);
    setPartialErrors([]);
    setLoadingPhase('roster');

    const run = async () => {
      // ── Phase 1: roster ──────────────────────────────────────────
      let rosterData;
      try {
        rosterData = await getTeamRoster(abbr, { signal });
      } catch (err) {
        if (signal.aborted || isAbortError(err)) return;
        setError(err);
        setLoadingPhase('idle');
        return;
      }
      if (signal.aborted || activeAbbrRef.current !== abbr) return;
      setRoster(rosterData);
      const rosterArr = Array.isArray(rosterData?.roster) ? rosterData.roster : [];
      const rosterCount = typeof rosterData?.count === 'number'
        ? rosterData.count
        : rosterArr.length;
      setProfilesTotal(rosterCount);
      setLoadingPhase('leaderboards');

      // ── Phase 2: 6 paired leaderboards in parallel ───────────────
      const lbResults = await Promise.allSettled(
        LEADERBOARD_METRICS.map((metric) => (
          getTeamLeaderboard(abbr, metric, {
            season: SEASON, paired: true, signal,
          })
        )),
      );
      if (signal.aborted || activeAbbrRef.current !== abbr) return;

      const nextLeaderboards = { ...EMPTY_LEADERBOARDS };
      const lbErrors = [];
      lbResults.forEach((result, i) => {
        const metric = LEADERBOARD_METRICS[i];
        if (result.status === 'fulfilled') {
          nextLeaderboards[metric] = result.value;
        } else if (!isAbortError(result.reason)) {
          lbErrors.push({ kind: 'leaderboard', metric, error: result.reason });
        }
      });
      setLeaderboards(nextLeaderboards);
      if (lbErrors.length) setPartialErrors((prev) => [...prev, ...lbErrors]);
      setLoadingPhase('profiles');

      // ── Phase 3: player profiles, capped concurrency ─────────────
      const profileItems = rosterArr
        .map((row) => row?.mlbam_id)
        .filter((id) => id != null);
      // Some rosters carry duplicate IDs (e.g. two-way players). Dedupe.
      const seenIds = new Set();
      const uniqueProfileItems = profileItems.filter((id) => {
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });

      // If the roster shrank vs `count`, sync the total to actual
      // profile-fetch denominator so the indicator doesn't get stuck
      // at "39/40".
      setProfilesTotal(uniqueProfileItems.length || rosterCount);

      const profileErrors = [];
      await runWithConcurrency(
        uniqueProfileItems,
        PROFILE_CONCURRENCY,
        signal,
        (mlbamId, sig) => getPlayerProfile(mlbamId, { season: SEASON, signal: sig }),
        ({ item, value, error: err }) => {
          if (signal.aborted || activeAbbrRef.current !== abbr) return;
          if (err) {
            profileErrors.push({ kind: 'profile', mlbam_id: item, error: err });
          } else if (value) {
            // Functional update so concurrent settles don't clobber.
            setProfiles((prev) => {
              const next = new Map(prev);
              next.set(item, value);
              return next;
            });
          }
          setProfilesLoaded((prev) => prev + 1);
        },
      );
      if (signal.aborted || activeAbbrRef.current !== abbr) return;

      if (profileErrors.length) {
        setPartialErrors((prev) => [...prev, ...profileErrors]);
      }

      // Cache the full dataset. Snapshot via setState callbacks to avoid
      // stale-closure values for `profiles` (which has been mutated through
      // many intermediate updates).
      setProfiles((prevProfiles) => {
        setCached(abbr, {
          roster: rosterData,
          leaderboards: nextLeaderboards,
          profiles: prevProfiles,
          profilesTotal: uniqueProfileItems.length || rosterCount,
          profilesLoaded: uniqueProfileItems.length,
          partialErrors: [...lbErrors, ...profileErrors],
        });
        return prevProfiles;
      });
      setLoadingPhase('done');
    };

    run();

    return () => controller.abort();
  }, [abbr, refetchTick]);

  // Final unmount safety — the per-effect cleanup already aborts, but a guard
  // here protects against any provider-level unmount that races the effect.
  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const value = useMemo(() => ({
    team,
    setTeam,
    abbr,
    name,
    data: {
      roster,
      leaderboards,
      profiles,
    },
    loadingPhase,
    loadingProgress: { profilesLoaded, profilesTotal },
    error,
    partialErrors,
    refetch,
  }), [
    team, setTeam, abbr, name,
    roster, leaderboards, profiles,
    loadingPhase, profilesLoaded, profilesTotal,
    error, partialErrors, refetch,
  ]);

  return (
    <MlbTeamDataContext.Provider value={value}>
      {children}
    </MlbTeamDataContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* Hooks                                                                      */
/* -------------------------------------------------------------------------- */

export function useMlbTeamData() {
  const ctx = useContext(MlbTeamDataContext);
  if (!ctx) {
    throw new Error('useMlbTeamData must be used within a <MlbTeamDataProvider>');
  }
  return ctx;
}

/**
 * useTeamRoster — convenience wrapper for pages that previously called
 * `getTeamRoster` directly.
 */
export function useTeamRoster() {
  const { data, loadingPhase } = useMlbTeamData();
  const roster = data.roster;
  return {
    roster: Array.isArray(roster?.roster) ? roster.roster : [],
    count: typeof roster?.count === 'number'
      ? roster.count
      : (Array.isArray(roster?.roster) ? roster.roster.length : 0),
    loading: !roster && (loadingPhase === 'idle' || loadingPhase === 'roster'),
  };
}

/**
 * useTeamLeaderboard — slice the cached `data.leaderboards` map by metric.
 */
export function useTeamLeaderboard(metric) {
  const { data, loadingPhase } = useMlbTeamData();
  const lb = data.leaderboards?.[metric] || null;
  const rawRows = Array.isArray(lb?.rows) ? lb.rows : [];
  const rows = rawRows.map((r) => ({
    name: r.player ?? r.name ?? r.player_name ?? r.pitcher_name ?? r.batter_name ?? '—',
    value: r.value ?? r.metric_value ?? r.stat ?? null,
    paired_value: r.paired_value ?? null,
    mlbam_id: r.mlbam_id ?? r.player_id ?? null,
  }));
  return {
    rows,
    paired_metric: lb?.paired_metric ?? null,
    metric: lb?.metric ?? metric,
    team: lb?.team ?? null,
    loading: !lb && (loadingPhase === 'idle' || loadingPhase === 'roster' || loadingPhase === 'leaderboards'),
  };
}

/**
 * useTeamPlayerProfile — read a player profile out of the cascade cache.
 */
export function useTeamPlayerProfile(mlbamId) {
  const { data, loadingPhase } = useMlbTeamData();
  const profile = mlbamId != null ? data.profiles.get(mlbamId) || null : null;
  return {
    profile,
    // True until the cascade completes OR this id has resolved.
    loading: !profile && loadingPhase !== 'done' && loadingPhase !== 'idle',
  };
}

// Test seam: deterministic provider for unit tests. Not used in app code.
const DEFAULT_MOCK = {
  team: 'orioles', setTeam: () => {}, abbr: 'BAL', name: 'Baltimore Orioles',
  data: { roster: null, leaderboards: EMPTY_LEADERBOARDS, profiles: new Map() },
  loadingPhase: 'done', loadingProgress: { profilesLoaded: 0, profilesTotal: 0 },
  error: null, partialErrors: [], refetch: () => {},
};

export function MockMlbTeamDataProvider({ value, children }) {
  const merged = { ...DEFAULT_MOCK, ...value, data: { ...DEFAULT_MOCK.data, ...(value && value.data) } };
  return <MlbTeamDataContext.Provider value={merged}>{children}</MlbTeamDataContext.Provider>;
}

export default MlbTeamDataContext;
